
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { callLegalAssistant } from "../services/aiService";
import type { AIMode, AIDataSource, AttachmentRef } from "../types/ai";
import { useToast } from "../components/Toast";

export interface LegalAIContextPayload {
  contextType: "contract" | "case" | "officialDoc" | "sealRequest" | "seal" | "governance" | "template" | "other" | "general" | string;
  contextId?: string;
  contextTitle?: string;
  
  // Data Sources
  currentFields?: any;
  templateText?: string;
  attachments?: AttachmentRef[];
}

export function useLegalAIHelper(
  payload: LegalAIContextPayload | null,
  options?: { 
    onApplyResult?: (target: string, content: string) => void 
  }
) {
  const { user } = useAuth();
  const { showToast } = useToast();

  const [isOpen, setIsOpen] = useState(false);
  const [activeMode, setActiveMode] = useState<AIMode>("summary");
  const [dataSource, setDataSource] = useState<AIDataSource>("attachments");
  
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [responseText, setResponseText] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const openHelper = (mode: AIMode = "summary") => {
    setActiveMode(mode);
    setIsOpen(true);
    setErrorMessage("");
    setResponseText("");
  };

  const closeHelper = () => {
    setIsOpen(false);
  };

  const callAI = async (tempFiles?: AttachmentRef[]) => {
    // 1. Basic Check
    if (!user) {
      showToast("請先登入", "error");
      return;
    }

    setErrorMessage("");
    setResponseText("");
    setIsLoading(true);

    try {
        // 2. Prepare Attachments
        const effectiveAttachments = [
          ...(payload?.attachments || []),
          ...(tempFiles || [])
        ];

        // Sanitize: Ensure name exists for backend
        const sanitizedAttachments = effectiveAttachments.map(f => {
             let name = f.name;
             if (!name && f.storagePath) {
                 const parts = f.storagePath.split('/');
                 name = parts[parts.length - 1];
             }
             return { ...f, name: name || "unknown_file" };
        });

        // If mode requires attachments, check availability
        if (dataSource === 'attachments' && sanitizedAttachments.length === 0 && !notes) {
            throw new Error("請上傳檔案或輸入指令。");
        }

        // 3. Auto-Inject Default Prompt if empty
        let userInstruction = notes.trim();
        if (!userInstruction && sanitizedAttachments.length > 0) {
            switch (activeMode) {
                case "summary":
                    userInstruction = "請以條列式摘要文件重點，列出條款結構與關鍵義務。";
                    break;
                case "analysis":
                    userInstruction = "請找出主要風險、可能爭點、缺漏資訊，並提出建議。";
                    break;
                case "risk":
                    userInstruction = "請用風險等級(低/中/高)分類，列出原因與對策。";
                    break;
                case "decision":
                    userInstruction = "請提供可採/不可採決策點與談判用語建議。";
                    break;
                case "draft":
                    userInstruction = "請依文件內容草擬可直接貼進合約的條款修正文案。";
                    break;
            }
        }

        // 4. Determine Context (Fallback to GENERAL if missing)
        const cType = (payload?.contextType || "GENERAL").toUpperCase();
        const cId = payload?.contextId || undefined; // Let backend handle null/undefined

        console.log(`[AI Helper] Calling with Mode: ${activeMode}, Files: ${sanitizedAttachments.length}`);

        // 5. Call Unified Service
        const res = await callLegalAssistant({
            mode: activeMode,
            attachments: sanitizedAttachments, // Pass array
            contextType: cType,
            contextId: cId,
            userInstruction: userInstruction 
        });

        // 6. Handle Response
        if (!res.ok) {
            throw new Error(res.message || "AI 服務回傳未知的錯誤。");
        } 
        
        if (!res.text) {
            throw new Error("AI 回傳內容為空，請稍後再試。");
        }

        setResponseText(res.text);

    } catch (e: any) {
        console.error("[AI Helper] Error:", e);
        setErrorMessage(e.message || "發生未預期錯誤，請檢查網路或權限。");
        showToast(`AI 分析發生錯誤: ${e.message}`, "error");
    } finally {
        setIsLoading(false);
    }
  };

  const applyResultToField = (target: string) => {
    if (options?.onApplyResult && responseText) {
      options.onApplyResult(target, responseText);
      showToast(`已寫入 ${target}`);
    } else {
      showToast("無法寫入：未設定回調或無內容", "error");
    }
  };

  return {
    isOpen,
    openHelper,
    closeHelper,
    activeMode,
    setActiveMode,
    dataSource,
    setDataSource,
    notes,
    setNotes,
    isLoading,
    responseText,
    errorMessage,
    callAI,
    applyResultToField,
    canUseFields: !!payload?.currentFields,
    canUseTemplate: !!payload?.templateText,
    canUseAttachments: true
  };
}
